import type { Title as UspList } from '../../../../amplienceTypes/schemas/exported/usp-list-schema';
import type { PageProperties } from '../../../../amplienceTypes/schemas/imported/generic-layout-slot-schema';
import type { GlobalTopNavigation as GlobalTopNavigationProps } from '../../../../amplienceTypes/schemas/imported/global-top-navigation-schema';
import type { MasterLayout as MasterLayoutProps } from '../../../../amplienceTypes/schemas/imported/master-layout-schema';
import type { ReactElement, ReactNode } from 'react';

import { useRouter } from 'next/compat/router';
import dynamic from 'next/dynamic';
import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { AndroidAppBanner } from './AndroidAppBanner';
import { NativeAppBanner } from './NativeAppBanner';
import { SmartBanner } from './SmartBanner';
import { COOKIE_NOTICE } from '../../../../utilities/constants/localStorageKeys';
import { CartProvider } from '../../../../utilities/context/dynamic/CartContext';
import { useCookieContext } from '../../../../utilities/context/dynamic/CookieContext';
import { useGalleryContext } from '../../../../utilities/context/dynamic/GalleryContext';
import { useNavActionContext } from '../../../../utilities/context/dynamic/NavActionContext';
import { WishlistProvider } from '../../../../utilities/context/dynamic/WishlistContext';
import { useAppContext } from '../../../../utilities/context/static/AppContext';
import { useStaticContext } from '../../../../utilities/context/static/StaticContext';
import { storageAvailable } from '../../../../utilities/helpers';
import { parseLocale } from '../../../../utilities/parsers';
import { AppliedVoucherNotification } from '../../../plp/globalNotification/AppliedVoucherNotification';
import { AccessibilityLabels } from '../../accessibilityLabels/AccessibilityLabels';
import { SideNavHeader } from '../../header/sideBar/SideNavHeader';
import { SideNavigation } from '../../header/sideBar/SideNavigation';
import { TopNavigation } from '../../header/TopNavigation';
import { HeaderUSP } from '../../headerUSP/HeaderUSP';
import { sideNavTransitionProperties } from '../../sideNavAnimationStyles';
import { Exponea } from '../../vendors/Exponea';
import { Forter } from '../../vendors/Forter';
import { GTMHeadScript } from '../../vendors/GTM';
import { KameleoonHead } from '../../vendors/kameleoon/KameleoonHead';
import { KameleoonScript } from '../../vendors/kameleoon/KameleoonScript';
import { OneTrust } from '../../vendors/OneTrust';
import { useAlgoliaInsights } from '../../vendors/useAlgoliaInsights';
import { HideIn } from '../HideIn';
import { media } from '../styles';
import type { GlobalAppStoreLinks } from '~/amplienceTypes/schemas/imported/global-app-store-links-schema';
import type { GlobalFooterNavigation } from '~/amplienceTypes/schemas/imported/global-footer-navigation-schema';
import type { GlobalTopNavigation2 } from '~/amplienceTypes/schemas/imported/global-top-navigation-schema';
import {
  useMobileTopLevelNavigationActive,
  useTopLevelNavigationEnabled,
} from '~/components/shared/header/topLevelNavigation/hooks/useTopLevelNavigationEnabled';
import { PageTypes } from '~/utilities/constants';
import { useConsentContext } from '~/utilities/context/dynamic/ConsentContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { pushToAppSettings } from '~/utilities/helpers/syncGlobalObject';

const MobileTopLevelNavigation = dynamic(
  async () =>
    (await import('~/components/shared/header/topLevelNavigation/mobile/MobileTopLevelNavigation'))
      .MobileTopLevelNavigation,
  { ssr: false }
);

const SideNavigationBottom = dynamic(
  async () => (await import('../../header/sideBar/SideNavigationBottom')).SideNavigationBottom,
  { ssr: false }
);

const Footer = dynamic(async () => (await import('../../footer/Footer')).Footer, { ssr: false });

const AudioEye = dynamic(async () => import('../../vendors/AudioEye').then(mod => mod.AudioEye), {
  ssr: false,
});

const ExponeaPushNotificationsPopup = dynamic(
  async () =>
    (await import('~/utilities/vendors/exponea/ExponeaPushNotificationsPopup'))
      .ExponeaPushNotificationsPopup,
  { ssr: false }
);

const ChatSupport = dynamic(
  async () => (await import('../../chatSupport/ChatSupport')).ChatSupport,
  { ssr: true }
);

const ExitIntentBannerModal = dynamic(
  async () => import('../../../shared/exitIntentBanner/ExitIntentBannerModal'),
  { ssr: false }
);

const CookieWallSettings = dynamic(
  async () => (await import('../../cookie/CookieWallSettings')).CookieWallSettings,
  { ssr: false }
);
const CookieNotice = dynamic(async () => (await import('../../cookie/CookieNotice')).CookieNotice, {
  ssr: false,
});
const Usabilla = dynamic(async () => (await import('../../vendors/Usabilla')).Usabilla, {
  ssr: false,
});
const NewsletterOverlay = dynamic(
  async () => (await import('../../newsletter/NewsletterOverlay')).NewsletterOverlay,
  { ssr: false }
);

const BackToTop = dynamic(async () => (await import('./BackToTop')).BackToTop, { ssr: false });

const AddToBagNotification = dynamic(
  async () => (await import('../../header/miniCart/AddToBagNotification')).AddToBagNotification,
  { ssr: false }
);

const S = {
  Container: styled.div<{
    $galleryZoomIn: boolean;
  }>`
    ${({ theme, $galleryZoomIn }) => css`
      display: flex;
      flex-direction: column;
      position: relative;
      min-height: 100vh;
      height: initial;
      max-height: ${$galleryZoomIn ? '100vh' : 'fit-content'};
      background-color: ${theme.colors.background.base};
      overflow: clip;

      @media ${media(theme).greaterThan('lg')} {
        max-height: fit-content;
      }
    `}
  `,

  Main: styled.main<{
    $sideNavOpen: boolean;
    $isPDP: boolean;
  }>`
    ${({ theme, $sideNavOpen, $isPDP }) => css`
      ${sideNavTransitionProperties(['left', 'width', 'transform'])}

      box-sizing: border-box;
      position: relative;
      left: var(--side-nav-width);
      width: 100%;
      flex: 1 0 auto;
      background-color: ${!$isPDP ? theme.colors.background.base : 'initial'};
      padding-top: calc(var(--top-nav-height) + var(--smart-banner-height));
      z-index: 501;

      @media ${media(theme).greaterThan('sm')} {
        padding-top: var(--top-nav-height);
        transform: none;
      }

      @media ${media(theme).greaterThan('xl')} {
        width: calc(100% - var(--side-nav-width));

        .has-open-sideNavigation & {
          width: calc(100vw - var(--side-nav-width) - var(--scrollbar-width));
        }
      }

      .is-highstreet & {
        --top-nav-height: 0px;

        padding-top: var(--smart-banner-height);
      }

      ${$sideNavOpen &&
      css`
        @media ${media(theme).between('lg', 'xl')} {
          left: 0;
          transform: translateX(var(--side-nav-width));
        }
      `}
    `}
  `,

  SideNavigationContainer: styled.div<{ $topLevelNavigationEnabled: boolean }>`
    ${({ theme, $topLevelNavigationEnabled }) => css`
      display: contents;

      ${$topLevelNavigationEnabled &&
      css`
        @media ${media(theme).greaterThan('lg')} {
          display: none;
        }
      `}
    `}
  `,
};

export type OpenedModal = 'locale-selector' | 'message-box-notification' | null;
export interface HeaderOpenModalProps {
  openedModal: OpenedModal;
  setOpenedModal: (val: OpenedModal) => void;
}

interface LayoutProps {
  pageProperties?: PageProperties;
  children: ReactNode;
  pageType: PageTypes;
  uspListContent?: UspList;
  forceExponea?: boolean;
}

export function MasterLayout({
  children,
  pageType,
  forceExponea,

  // Data From Amplience
  topNavigation,
  footerNavigation,
  cookieWallSettings,
  cookieNotice,
  backToTopBtn,
  loginLabel,
  messageBoxNotification,
  accessibilityLabels,
  sfscChat,
  uspListContent,
  pageProperties,
  exponeaPopup,
  newsletterOverlay,
}: LayoutProps & MasterLayoutProps): ReactElement {
  const router = useRouter();
  const [showCookieWall, setShowCookieWall] = useState(false);
  const [showCookieNotice, setShowCookieNotice] = useState(false);
  const [loadCmp, setLoadCmp] = useState(false);
  const [cmpDomainIdValue, setCmpDomainIdValue] = useState('');
  const [openedModal, setOpenedModal] = useState<OpenedModal>(null);
  const { country, locale, language, isGStar, siteType } = useAppContext();
  const { cocoCookie, oneTrustCookie, setHasInteracted } = useCookieContext();
  const { galleryZoomIn } = useGalleryContext();
  const { sideNavOpened } = useNavActionContext();
  const {
    configuration: {
      enableCookieWall,
      enableExponea,
      isBodyOnlyView,
      isLineSession,
      enableCMP,
      cmpDomainId,
    },
    topLevelNavigationContent,
  } = useStaticContext();
  const { desktop: topLevelNavigationEnabled } = useTopLevelNavigationEnabled();
  const mobileTopLevelNavigationActive = useMobileTopLevelNavigationActive();
  const isPDP = pageType === PageTypes.PRODUCT;

  // Reuse the footer's (currently unrendered) GlobalAppStoreLinks copy for the native
  // Android banner — already localized, so no extra content type is needed.
  const appStoreLinksContent = (footerNavigation?.content as GlobalFooterNavigation | undefined)
    ?.appStoreLinks?.content as GlobalAppStoreLinks | undefined;

  const { hasAnalyticsConsent, hasFullConsent } = useConsentContext();
  const { initialize: initAlgoliaInsights } = useAlgoliaInsights();

  // only show the newsletter when either the coco or the onetrust cookie is available
  const cookieConsentExists = !!cocoCookie || !!oneTrustCookie;

  const closeCookieNotice = () => {
    if (storageAvailable('localStorage')) {
      localStorage.setItem(COOKIE_NOTICE, 'true');
    }

    setShowCookieNotice(false);
  };

  const onCloseCookieWall = () => {
    setShowCookieWall(false);
    setHasInteracted();
  };

  useEffect(() => {
    if (!enableCMP) {
      if (enableCookieWall) {
        setShowCookieWall(!cocoCookie);
        setShowCookieNotice(false);
      } else {
        setShowCookieWall(false);
        setShowCookieNotice(
          storageAvailable('localStorage') ? !localStorage.getItem(COOKIE_NOTICE) : true
        );
      }
    } else {
      setLoadCmp(!!enableCMP);
      setCmpDomainIdValue(cmpDomainId || '');

      setShowCookieNotice(false);
      setShowCookieWall(false);
    }
  }, [cocoCookie, enableCookieWall, enableCMP, cmpDomainId]);

  useEffect(() => {
    if (hasAnalyticsConsent) {
      initAlgoliaInsights();
    }
  }, [hasAnalyticsConsent, initAlgoliaInsights]);

  useEffect(() => {
    window.ampliencePageProperties = pageProperties;
  }, [pageProperties]);

  useEffect(() => {
    const { languageIso } = parseLocale(locale);

    pushToAppSettings({
      enableCookieWall,
      consentCookieValue: cocoCookie || '00000',
      isBodyOnlyView,
      isBodyOnlyWebview: isBodyOnlyView,
      registration: { isLineSession },
      languageIso,
      language,
      country,
    });
  }, [
    cocoCookie,
    country,
    enableCookieWall,
    isBodyOnlyView,
    isLineSession,
    language,
    locale,
    router?.query?.q,
  ]);

  return (
    <>
      <HideIn>{loadCmp && cmpDomainIdValue && <OneTrust cmpDomainId={cmpDomainIdValue} />}</HideIn>
      {pageProperties?.liveChatEnabled !== false && (
        <ChatSupport sfscChatContent={sfscChat?.content} />
      )}
      <GTMHeadScript />
      <KameleoonHead />
      <HideIn>
        {pageProperties?.smartAppBannerEnabled !== false ? (
          <SmartBanner />
        ) : (
          (isGStar || siteType === SiteType.Aaf) && (
            <>
              <NativeAppBanner />
              <AndroidAppBanner
                title={appStoreLinksContent?.title}
                description={appStoreLinksContent?.text}
                ctaLabel={appStoreLinksContent?.findOutMore}
              />
            </>
          )
        )}
      </HideIn>
      <AudioEye />
      <Forter />
      {(hasFullConsent || forceExponea) && enableExponea && <Exponea />}
      {enableExponea && exponeaPopup?.content && (
        <ExponeaPushNotificationsPopup {...exponeaPopup.content} />
      )}
      {!isBodyOnlyView &&
        newsletterOverlay?.content &&
        cookieConsentExists &&
        !pageProperties?.excludeNewletterOverlayPageview && (
          <NewsletterOverlay {...newsletterOverlay.content} />
        )}
      <S.Container suppressHydrationWarning $galleryZoomIn={galleryZoomIn}>
        {accessibilityLabels?.content && <AccessibilityLabels {...accessibilityLabels.content} />}
        <CartProvider content={(topNavigation?.content as GlobalTopNavigation2)?.cart?.content}>
          <WishlistProvider
            content={(topNavigation?.content as GlobalTopNavigation2)?.wishlist?.content}
          >
            <HideIn>
              <ExitIntentBannerModal />
              {topNavigation?.content && (
                <TopNavigation
                  showCookieWall={showCookieWall}
                  loginLabel={loginLabel || ''}
                  messageBoxNotification={messageBoxNotification}
                  openedModal={openedModal}
                  setOpenedModal={setOpenedModal}
                  topLevelNavigation={topLevelNavigationContent?.categories}
                  {...topNavigation.content}
                />
              )}

              <S.SideNavigationContainer $topLevelNavigationEnabled={topLevelNavigationEnabled}>
                <SideNavigation
                  mobileDialogDisabled={mobileTopLevelNavigationActive}
                  header={
                    <SideNavHeader
                      localeSelector={
                        (topNavigation?.content as GlobalTopNavigationProps)?.localeSelector
                      }
                      openedModal={openedModal}
                      setOpenedModal={setOpenedModal}
                      _meta={{ schema: '' }}
                    />
                  }
                >
                  <SideNavigationBottom
                    loginLabel={loginLabel}
                    messageBoxNotification={messageBoxNotification}
                    openedModal={openedModal}
                    setOpenedModal={setOpenedModal}
                  />
                </SideNavigation>
              </S.SideNavigationContainer>

              {/* Only while in use (switch on, below `lg`): no drawer code or state on desktop. */}
              {mobileTopLevelNavigationActive && (
                <MobileTopLevelNavigation
                  searchLabel={
                    (topNavigation?.content as GlobalTopNavigationProps)?.searchBoxPlaceholder
                  }
                  localeSelector={
                    (topNavigation?.content as GlobalTopNavigationProps)?.localeSelector?.content
                  }
                  messageBoxNotification={messageBoxNotification}
                  openedModal={openedModal}
                  setOpenedModal={setOpenedModal}
                />
              )}
            </HideIn>

            <S.Main
              suppressHydrationWarning
              $sideNavOpen={sideNavOpened}
              id="container"
              data-testid="main-content"
              tabIndex={-1}
              $isPDP={isPDP}
            >
              {uspListContent && <HeaderUSP {...uspListContent} />}
              <AppliedVoucherNotification />
              {children}
            </S.Main>
            {(topNavigation?.content as GlobalTopNavigation2)?.cart?.content && (
              <AddToBagNotification />
            )}
          </WishlistProvider>
        </CartProvider>

        <HideIn>
          {pageProperties?.displayFooter !== false && (
            <Footer pageProperties={pageProperties} footerNavigation={footerNavigation} />
          )}
        </HideIn>

        <BackToTop backToTopBtn={backToTopBtn} />

        <HideIn>
          {enableCMP && cmpDomainId
            ? null
            : showCookieWall &&
              cookieWallSettings?.content && (
                <CookieWallSettings
                  enableCMP={enableCMP}
                  close={onCloseCookieWall}
                  {...cookieWallSettings.content}
                />
              )}
          {enableCMP && cmpDomainId
            ? null
            : cookieNotice?.content && (
                <CookieNotice
                  showCookieNotice={showCookieNotice}
                  close={closeCookieNotice}
                  {...cookieNotice.content}
                />
              )}
        </HideIn>
      </S.Container>
      {pageProperties?.feedbackButtonEnabled !== false && <Usabilla locale={locale} />}
      <KameleoonScript />
    </>
  );
}
